import React from 'react';
import { useHistory } from 'react-router-dom';
import {
  Box, Button, CircularProgress, Typography,
} from '@mui/material';
import { LoanPayoutProps } from '../../types/loanPayment';
import analytics from '../../utils/analytics';
import styles from './styles';

export default function LoanPayout(props: LoanPayoutProps) {
  const {
    pendingInstallments,
    collectionServiceUrl,
    isByRut,
    prepayIsMoreExpensive,
    showPrepayAdvanceHint = true,
    allowAutoRedirect = true,
    isAggregateSubpanel = false,
  } = props;
  const { quantity, amount } = pendingInstallments;

  const pendingInstallmentsText = quantity > 1 ? 'cuotas' : 'cuota';
  const history = useHistory();
  const shouldAutoRedirect = isByRut && allowAutoRedirect;

  const getPendingInstallmentsDetailText = () => {
    switch (quantity) {
      case 0:
        if (prepayIsMoreExpensive) {
          return (
            <div />
          );
        }
        if (!showPrepayAdvanceHint) {
          return (
            <div />
          );
        }
        return (
          <Typography variant="caption">
            Por favor considera que si adelantas todas las cuotas que te quedan,
            no estarás ahorrando y en ese caso te conviene
            {' '}
            <b>Prepagar crédito</b>
            .
          </Typography>
        );
      case 1:
        return (
          <Typography variant="caption">
            Tienes
            {' '}
            <b>1</b>
            {' '}
            cuota pendiente de pago.
          </Typography>
        );
      default:
        return (
          <Typography variant="caption">
            Tienes
            {' '}
            <b>{quantity}</b>
            {' '}
            cuotas pendientes de pago.
          </Typography>
        );
    }
  };

  const pendingInstallmentsDetailText = getPendingInstallmentsDetailText();

  const payButtonLabel = (() => {
    if (quantity > 0) {
      return `Pagar ${pendingInstallmentsText}`;
    }
    if (showPrepayAdvanceHint) {
      return 'Adelantar cuota(s)';
    }
    return 'Ir al portal de pago';
  })();

  const handleAction = () => {
    if (collectionServiceUrl) {
      analytics.track('Click Boton', {
        text: payButtonLabel,
        initiator: 'usuario',
      });
      window.location.href = collectionServiceUrl;
    }
  };

  React.useEffect(() => {
    if (shouldAutoRedirect && collectionServiceUrl && !pendingInstallments.hasActiveInjections) {
      setTimeout(() => { window.location.href = collectionServiceUrl; }, 1000);
    }
  }, [shouldAutoRedirect, collectionServiceUrl, pendingInstallments.hasActiveInjections]);

  if (shouldAutoRedirect && collectionServiceUrl && !pendingInstallments.hasActiveInjections) {
    return (
      <Box sx={styles.loanPayoutContainer}>

        <Typography color="success" variant="body2" sx={{ fontStyle: 'italic', marginBottom: 5 }}>
          Te estamos redirigiendo a la página de pago...
        </Typography>
        <CircularProgress color="secondary" size={25} />
      </Box>
    );
  }

  if (shouldAutoRedirect && !collectionServiceUrl
    && !pendingInstallments.hasActiveInjections
    && quantity === 0) {
    if (isAggregateSubpanel) {
      return (
        <Box sx={styles.loanPayoutContainer}>
          <Typography variant="body2" sx={{ marginBottom: 2 }}>
            No tienes cuotas pendientes de pago por este medio.
          </Typography>
        </Box>
      );
    }
    return (
      <Box sx={styles.loanPayoutContainer}>
        <Typography variant="body1" sx={{ marginBottom: 5 }}>
          No tienes cuotas pendientes de pago con
          {' '}
          <Typography variant="inherit" component="span" color="secondary" sx={{ fontWeight: 'bold' }}>
            banca.me.
          </Typography>
        </Typography>
        <Button variant="outlined" color="primary" size="large" sx={styles.loanPayoutButton} onClick={() => history.push('/account')}>
          Ir a mi cuenta
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={styles.loanPayoutContainer}>
      {quantity === 0 && showPrepayAdvanceHint && (
      <Typography variant="body2" sx={{ fontWeight: 'bold', marginBottom: 5 }}>
        No tienes cuotas pendientes de pago, pero puedes adelantar una o más cuotas si quieres.
      </Typography>
      )}
      <Button variant="contained" color="primary" size="large" sx={styles.loanPayoutButton} onClick={handleAction} disabled={!collectionServiceUrl}>
        {payButtonLabel}
      </Button>
      {pendingInstallmentsDetailText}
      {quantity > 0 && (
      <Typography variant="caption" gutterBottom sx={{ fontWeight: 'bold' }}>
        Total a pagar:
        {' '}
        <Typography variant="caption" color="secondary" sx={{ fontWeight: 'bold' }}>
          $
          {' '}
          {amount.toLocaleString('es-CL')}
        </Typography>
      </Typography>
      )}
    </Box>
  );
}
